import React, {useState} from "react";
import {getBadgeTitle, getCriteriaLabel, hasPromotion,} from "../../data/attributeHelper.js";
import {find, get, startCase} from "lodash";
import moment from "moment";
import * as analytics from "../../analytics";
import Link from "next/link";
import cn from "classnames";
import {getFormattedContactNumber} from "../../utils/utilities";

// Components
import IndividualTilesServices from "./IndividualTilesServices";
import TopRatedIndividualTileLabel from "../Search/TopRatedIndividualTileLabel";
import Button from "../../components/Inputs/Button";
import ProfessionalTypeIndividualTileBadge from "../Search/ProfessionalTypeIndividualTileBadge";
import IndividualTileBadge from "../Search/IndividualTileBadge";

// Constants
import {PROFESSIONAL_TYPE} from "../../__constants__";
import {defaultPictureAvatar} from "../../assets/images";

// Icons
import LocationPinIcon from "../vectors/LocationPinIcon";
import TimeIcon from '../vectors/TimeIcon';
import ShieldIcon from '../vectors/ShieldIcon';
import SettingsIcon from '../vectors/SettingsIcon';
import CheckIcon from '../vectors/CheckIcon';
import PhoneIcon from '../vectors/PhoneIcon';
import StarRatingDisplay from "../StarRatingDisplay";
import Tooltip from "../Tooltip.jsx";
import QuestionMark from "../vectors/QuestionMark.jsx";

const professionalDetails = (professionalType) => {
  const professional = {
    isFinancialCoach: false,
    isProtectionAdviser: false,
    isEquityReleaseAdviser: false,
    isAccountant: professionalType === PROFESSIONAL_TYPE.ACCOUNTANT,
  };

  if (professionalType === PROFESSIONAL_TYPE.FINANCIAL_COACH) {
    return ({ ...professional, isFinancialCoach: true });
  }

  if (
    [
      PROFESSIONAL_TYPE.PROTECTION_LIMITED,
      PROFESSIONAL_TYPE.PROTECTION_WHOLE_OF_MARKET,
    ].includes(professionalType)
  ) {
    return ({ ...professional, isProtectionAdviser: true });
  }

  if (
    [
      PROFESSIONAL_TYPE.EQUITY_RELEASE_LIMITED,
      PROFESSIONAL_TYPE.EQUITY_RELEASE_WHOLE_OF_MARKET,
    ].includes(professionalType)
  ) {
    return ({ ...professional, isEquityReleaseAdviser: true });
  }

  return professional;
};

const handleGTM = (label) => {
  analytics.track("Result Tile", {
    category: "Search",
    label,
  });
};

const getFirstCharacters = (str, charCount) => {
  return str.slice(0, charCount).concat("... ");
};

const renderRecentReviewTooltip = (data) => {
  const recentReviewCount = get(
    data,
    "review_first_impression_count_in_12_months",
    0
  );

  return (
    <details className="group text-center flex flex-col max-xs:text-left">
        <summary className="!text-center text-sm sm:text-base list-none cursor-pointer max-xs:!text-left">
          {recentReviewCount} recent reviews <span className="pl-1 [&_svg]:-mb-1 [&_svg]:text-secondary-blue"><QuestionMark size='20px' /></span>
        </summary>

        <p className='text-sm m-0 my-2'>This is the number of reviews the adviser has received in the last 12 months</p>
    </details>
  );
};

const renderBadges = (list, professional) => {
  if (!list) return null;
  if (professional.isFinancialCoach) return null;
  if (professional.isProtectionAdviser) return null;
  if (professional.isEquityReleaseAdviser) return null;

  let hasFA = false;

  list.forEach((v) => {
    if ([550, 551].indexOf(v.id) > -1) hasFA = true;
  });

  return list.map((item) => {
    const { id } = item;

    if ([554, 555].indexOf(id) > -1 && hasFA) return null;

    const badgeTitle = getBadgeTitle(item);
    if (!badgeTitle) return null;

    return (
      <IndividualTileBadge
        icon={<ShieldIcon />}
        label={badgeTitle}
        key={id}
      />
    );
  });
};

const renderPromotionBadges = (item) => {
  const promotion = hasPromotion(item.id);

  if (!hasPromotion(item.id)) {
    return null;
  }

  return (
    <IndividualTileBadge
      icon={<TimeIcon />}
      label={promotion.label}
      key={item.id}
    />
  );
};

const renderCriteriaBadges = (item, services) => {
  const criteria = getCriteriaLabel(item.id);
  const criteriaLabel = get(criteria, ["label"], "");
  const offersMortgage =
    !!criteriaLabel.includes("Mortgage value") &&
    find(services, ["id", 392]);
  const offersSavings =
    !!criteriaLabel.includes("Savings/pensions") &&
    find(services, ["id", 397]);

  if (offersSavings || offersMortgage) {
    return (
      <IndividualTileBadge
        key={item.id}
        label={criteriaLabel}
        icon={<SettingsIcon />}
      />
    );
  }
};

const renderUnavailableText = () => (
  <div className={'flex items-center sm:flex-col h-full flex-row p-0 sm:pl-4'}>
    <p className={'text-primary-blue text-[1rem] font-bold leading-[1.167] m-0'}>Unavailable for contact&nbsp;</p>
    <p className={'text-primary-blue text-[1rem] font-bold leading-[1.167] m-0'}>
      through VouchedFor
    </p>
  </div>
);

export default function IndividualTiles(props) {
  const professional = professionalDetails(get(props, "data.type"));

  const isProtectionAdviser  = professional.isProtectionAdviser;
  const isEquityReleaseAdviser  = professional.isEquityReleaseAdviser;
  const isFinancialCoach  = professional.isFinancialCoach;
  const isAccountant = professional.isAccountant;

  const {data, showBestMatch, searchType} = props;
  const distance = get(data, "distance");
  const avatarPhoto = get(data, ["photos", "standard"], defaultPictureAvatar);
  const avatarAlt = avatarPhoto === defaultPictureAvatar
      ? 'A picture representing a professional'
      : `A picture of ${data.first_name} ${data.last_name}`;
  const profileLink = "/" + get(data, ["permalink", "url"], "#");
  const investmentMortgageAdvice = get(data, [
    "attribute_ids",
    "investment_and_mortgage_advice",
  ]);
  const verifiedSince = get(data, "verified_since", null);
  const verifiedSinceWording = professional.isAccountant ? 'VouchedFor member since' : 'Verified since';
  const services = get(data, ["attribute_ids", "services"]);
  const isTopRated = get(data, "is_top_rated");

  const toolTipText =
      "Our Best Match algorithm, which uses a combination of factors such as distance from your location and number of reviews, suggests this professional is the best suited to you based on your filter selections.";

  return (
      <>
        {/* Best Match tab */}
        {showBestMatch && (
            <div className={'flex justify-center mb-3 font-bold relative'}>
                <div className={
                    'bg-mint-green text-lg px-2 rounded-t-2xl'
                  }
                >
                  <p className={'my-2 flex'}>Best Match <span className={'text-sm pl-2 cursor-pointer [&_div]:!text-primary-blue [&_div]:pt-0.5'}><Tooltip titleText='more' content={toolTipText} /></span></p> 
                </div>
            </div>
        )}

        {/* Card Container */}
        <div className={cn({
          'w-[calc(100%+24px)] -m-[12px] flex flex-wrap box-border pt-2': true,
          'drop-shadow-lg shadow-lg bg-white': true,
          'border-4 border-solid border-mint-green': showBestMatch
        })}>
          {/* Avatar Container */}
          <div className={cn({
            'inline-flex sm:flex sm:flex-col flex-grow-0 items-center': true,
            'w-full basis-full sm:max-w-[16.666667%] sm:basis-1/6': true,
            'm-0 p-4 box-border': true,
          })}>
            {/* Avatar */}
            <div className={'box-content w-full sm:max-w-[130px] max-w-[88px] pr-2 sm:pr-0 text-center'}>
              <Link href={profileLink}>
                <a onClick={() => handleGTM("Photo")} className={'block'}>
                  <div className={"w-[calc(100%-14px)] max-w-[130px] sm:max-w-full relative inline-flex"}>
                    <div className={
                        'sm:w-full p-0.5 rounded-full ' +
                        'ring-[4px] ring-primary ring-offset-base-100 ring-offset-2 ring-mint-green'
                    }>
                      <img
                          src={avatarPhoto}
                          alt={avatarAlt}
                          className={"w-full h-full object-cover text-center rounded-[50%]"}
                      />
                    </div>
                  </div>
                </a>
              </Link>
            </div>

            <div className={'hidden sm:inline'}>
                <div className={'pt-3 text-center no-underline'}>
                  <Link href={profileLink + "#reviews"}>
                    <a
                        className={'cursor-pointer relative block sm:pt-0 md:pt-2' +
                            'after:absolute after:content=[\'""\']'}
                        onClick={() => handleGTM("Star_rating")}
                    >
                      <StarRatingDisplay
                          rating={Math.round(data.review_average_score)}
                          pixelSize={'20px'}
                          centered
                      />
                    </a>
                  </Link>
                </div>

                {/* Number of Reviews, Desktop */}
                <p className={'text-lg pt-2 m-0 text-center'}>
                  <Link
                      passHref
                      href={profileLink + "#reviews"}
                      className={'test-reviews'}
                  >
                    <b>{`${data.review_count} reviews`}</b>
                  </Link>
                </p>

                {renderRecentReviewTooltip(data)}

                {(data.review_average_score !== 0) &&
                    <Link href={profileLink + "#reviews"} passHref>
                      <a className={'w-full pt-2 text-center no-underline text-primary-blue text-sm block cursor-pointer'}>
                        {`${data.review_average_score} out of 5`}
                      </a>
                    </Link>
                }
            </div>


              <div className={'inline sm:hidden'}>
                <Link href={profileLink}>
                  <a
                      className={'no-underline'}
                      onClick={() => handleGTM("Name")}
                  >
                    <h6 className={'text-primary-blue full-name text-xl m-0'}>
                      {data.first_name} {data.last_name}
                    </h6>
                    <p className={'text-primary-blue m-0 text-sm test-firm-name'}>
                      {data.firm_name}
                    </p>
                  </a>
                </Link>

                <div className={'flex text-left items-center w-full pt-2 no-underline'}>
                  <div className={'max-w-full'}>
                    <Link href={profileLink + "#reviews"}>
                      <a
                          className={'cursor-pointer'}
                          onClick={() => handleGTM("Star_rating")}
                      >
                        <StarRatingDisplay
                          rating={Math.round(data.review_average_score)}
                          pixelSize={'12px'}
                        />
                      </a>
                    </Link>
                  </div>

                  <p className={'pl-[6px] m-0 text-[13px] md:text-[16px]'}>
                    <Link href={profileLink + "#reviews"}>
                      <a className={'test-reviews text-secondary-blue cursor-pointer no-underline'}>
                        {`${data.review_count} reviews`}
                      </a>
                    </Link>
                  </p>
                </div>

                <div className={'flex relative'}>
                  {renderRecentReviewTooltip(data)}
                </div>

              </div>
          </div>

          {/* Grid container */}
          <div className={cn({
            'flex flex-col flex-grow-0 sm:basis-5/6 sm:max-w-[83.333333%]': true,
            'relative border-box items-center overflow-hidden': true,
            'sm:ml-[1px] sm:-mr-[1px]': true // -1px to offset the world's most annoying disappearing pixel
          })}>
            <div className={'w-full flex flex-wrap box-border p-3'}>
              {/* Top Rated Banner Mobile */}
              <div className={'flex sm:hidden flex-wrap w-[calc(100%+16px)] p-1 -mx-2 mb-2'}>
                <TopRatedIndividualTileLabel
                    isTopRated={isTopRated}
                    variant="mobile"
                />
              </div>

                {/* Professional Name */}
                <div className={'flex-col justify-center items-start p-4 sm:p-0 hidden sm:flex basis-1/3 max-w-[33.333333%]'}>
                  <Link href={profileLink}>
                    <a
                        className={'no-underline text-primary-blue'}
                        onClick={() => handleGTM("Name")}
                    >
                      <h6 className={'text-primary-blue full-name text-xl m-0'}>
                        {data.first_name} {data.last_name}
                      </h6>
                    </a>
                  </Link>

                  <p className={'text-primary-blue test-firm-name m-0'}>
                    {data.firm_name}
                  </p>
                </div>

              {/* Phone Number */}
                <div className={cn({
                  'flex sm:inline-flex flex-grow self-start basis-full max-w-full sm:basis-1/3 sm:max-w-[33.333333%]': true,
                  'box-border': true
                })}>
                  {data.is_contactable && (
                      <>
                        <div className={'flex align-center fill-mint-green w-8 h-8 my-4 mr-4 sm:mx-4 box-border border-mint-green border-2 border-solid rounded-full p-1'}>
                          <PhoneIcon />
                        </div>

                        <Link href={`tel:${data.phone_number}`}>
                          <a className={'no-underline self-center'}>
                            <h5 className={'test-phone-number text-primary-blue m-0 text-base sm:text-[1.5rem]'}
                            >
                              {getFormattedContactNumber(data.phone_number)}
                            </h5>
                          </a>
                        </Link>
                      </>
                  )}
                </div>

                {/* Contact Button */}
                <div className={'hidden sm:flex flex-grow-0 items-center m-0 basis-1/3 max-w-[33.333333%]'}>
                  {data.is_contactable ? (
                      <Link href={`${profileLink}/enquiry`} passHref>
                        <Button
                            text={`Contact ${data.first_name}`}
                            className={'contact-button max-w-56 w-full h-full max-h-12 box-border text-center'}
                            onClick={() => handleGTM("Button - contact")}
                       />
                      </Link>
                  ) : (
                      renderUnavailableText()
                  )}
                </div>

                {/* Divider */}
                <div className={'flex-grow basis-full max-w-full box-border hidden sm:flex m-0'}>
                  <hr className={'w-full border-0 h-[1px] bg-contrast-grey flex-shrink-0'} />
                </div>

              {/* About Professional */}
              <div className={'items-start flex-grow-0 sm:basis-1/3 sm:max-w-["33.333333%"] m-0 box-border'}>
                {data.about && (
                    <p className={'sm:max-w-[300px] m-0 text-sm p-0 sm:p-4 sm:pl-0'}>
                      <div className={'inline sm:hidden'}>
                        {getFirstCharacters(data.about, 110)}
                      </div>

                      <div className={'hidden sm:inline'}>
                        {getFirstCharacters(data.about, 180)}
                      </div>

                      <Link href={profileLink}>
                        <a
                            onClick={() => handleGTM("profile more")}
                            className={'test-description no-underline text-secondary-blue cursor-pointer'}
                        >
                          more
                        </a>
                      </Link>
                    </p>
                )}

                {/* View Profile Button */}
                <div className={'hidden sm:block'}>
                  <Link
                      href={profileLink}
                      onClick={() => handleGTM("Button - view profile")}
                      passHref
                  >
                    <b className={'test-view-profile no-underline text-secondary-blue cursor-pointer'}>
                      VIEW PROFILE
                    </b>
                  </Link>
                </div>
              </div>

              {/* Badges */}
              <div className={cn({
                'w-full sm:max-w-["33.333333%"] basis-full sm:basis-1/3 self-start': true,
                'box-border m-0 py-4 sm:p-4 sm:pb-2': true,
              })}>
                <div>
                  {(isFinancialCoach ||
                      isProtectionAdviser ||
                      isEquityReleaseAdviser) && (
                      <ProfessionalTypeIndividualTileBadge
                          firstName={data.first_name}
                          type={data.type}
                      />
                  )}

                  {searchType === "location" && (
                      <IndividualTileBadge
                          label={`${startCase(
                              data.home_town?.toLowerCase()
                          )} (${distance.toFixed(2)} miles away)`}
                          className="test-location"
                          icon={<LocationPinIcon className={'fill-primary-blue'} />}
                      />
                  )}
                </div>

                {renderBadges(investmentMortgageAdvice, professional)}

                {(investmentMortgageAdvice || isAccountant) &&
                    verifiedSince && (
                        <IndividualTileBadge
                            icon={<CheckIcon className={'stroke-primary-blue'}/>}
                            label={`${verifiedSinceWording} ${moment(
                                data.verified_since
                            ).format("MMMM, YYYY")}`}
                        />
                    )}

                {get(data, ["attribute_ids", "promotions"]) && (
                    <div>
                      {get(data, ["attribute_ids", "promotions"]).map(
                          renderPromotionBadges
                      )}
                    </div>
                )}

                {get(data, [
                  "attribute_ids",
                  "wealth_and_mortgage_criteria",
                ]) && (
                    <div>
                      {get(data, [
                        "attribute_ids",
                        "wealth_and_mortgage_criteria",
                      ]).map((item) => renderCriteriaBadges(item, services))}
                    </div>
                )}
              </div>

              {/* Services Offered */}
              <div className={'pt-4 pb-12 lg:pb-4 flex-grow-0 max-w-[33.333333%] m-0 basis-1/3 hidden sm:block'}>
                {services && (
                    <IndividualTilesServices
                        services={services}
                        permalink={profileLink}
                        isFinancialCoach={isFinancialCoach}
                        handleGTM={handleGTM}
                    />
                )}
              </div>

              {/* Contact buttons */}
              <div className={'flex items-center w-full justify-center basis-full max-w-full pt-2 sm:hidden bg'}>
                {data.is_contactable ? (
                    <Link href={`${profileLink}/enquiry`} passHref>
                      <Button
                          text={`Contact ${data.first_name}`}
                          className="contact-button w-full text-center"
                          onClick={() => handleGTM("Button - contact")}
                      />
                    </Link>
                ) : (
                    renderUnavailableText()
                )}
              </div>

              {/* View profile button */}
              <div className={'flex items-center w-full justify-center basis-full max-w-full pt-2 sm:hidden'}>
                <Link href={profileLink} passHref>
                  <Button
                      text={'View profile'}
                      className="test-view-profile w-full text-center"
                      onClick={() => handleGTM("Button - view profile")}
                   />
                </Link>
              </div>
            </div>

            <div className={'hidden sm:inline'}>
              <TopRatedIndividualTileLabel isTopRated={isTopRated}/>
            </div>
          </div>
        </div>
      </>
  );
}
